import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { useSnackbar } from 'notistack';
import '../../utils/quillBlots';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { fetchCategories } from '../../store/slices/categoriesSlice';
import { createPost, updatePost, fetchPostById, clearCurrent } from '../../store/slices/postsSlice';
import { uploadPostMedia } from '../../api/postsApi';
import { getCategoryLabel } from '../../utils/categoryLabels';
import { getEmbedUrl } from '../../utils/youtubeEmbed';

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link', 'image', 'video', 'videoUpload',
  'align',
  'blockquote',
  'color', 'background',
];

export default function PostForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { list: categories } = useSelector((s) => s.categories);
  const { current: post, loading } = useSelector((s) => s.posts);
  const quillRef = useRef(null);
  const headerInputRef = useRef(null);
  const pendingInlineRef = useRef(new Map());
  const headlineBlobUrlRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    body: '',
    category: '',
    isNewsletter: false,
    headlineImage: null,
  });
  const [headlineFile, setHeadlineFile] = useState(null);
  const [headlinePreviewUrl, setHeadlinePreviewUrl] = useState(null);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState(null); // { phase, current, total, percent }

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image', 'video'],
          [{ align: [] }],
          ['blockquote'],
          [{ color: [] }, { background: [] }],
          ['clean'],
        ],
        handlers: {
          image: function imageHandler() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();
            input.onchange = () => {
              const file = input.files?.[0];
              if (!file) return;
              const blobUrl = URL.createObjectURL(file);
              pendingInlineRef.current.set(blobUrl, { file, type: 'image' });
              const quill = quillRef.current?.getEditor?.();
              if (quill) {
                const range = quill.getSelection(true) || { index: quill.getLength() };
                quill.insertEmbed(range.index, 'image', blobUrl);
                quill.setSelection(range.index + 1);
              }
            };
          },
          video: function videoHandler() {
            setEmbedUrl('');
            setEmbedDialogOpen(true);
          },
        },
      },
    }),
    []
  );

  const addInlineVideo = useCallback((file) => {
    const blobUrl = URL.createObjectURL(file);
    pendingInlineRef.current.set(blobUrl, { file, type: 'video' });
    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      const range = quill.getSelection(true) || { index: quill.getLength() };
      quill.insertEmbed(range.index, 'videoUpload', blobUrl);
      quill.setSelection(range.index + 1);
    }
  }, []);

  const handleEmbedVideo = () => {
    const embedSrc = getEmbedUrl(embedUrl);
    if (embedSrc) {
      const quill = quillRef.current?.getEditor?.();
      if (quill) {
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertEmbed(range.index, 'video', embedSrc);
        quill.setSelection(range.index + 1);
      }
      setEmbedDialogOpen(false);
      setEmbedUrl('');
      enqueueSnackbar('Vidéo intégrée.', { variant: 'success' });
    } else {
      enqueueSnackbar('URL non valide. Utilisez un lien YouTube ou Vimeo.', { variant: 'error' });
    }
  };

  const embedVideoInputRef = useRef(null);
  const handleAddVideoFromDialog = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEmbedDialogOpen(false);
    addInlineVideo(file);
    enqueueSnackbar('Vidéo ajoutée. Elle sera envoyée à la publication.', { variant: 'info' });
    if (embedVideoInputRef.current) embedVideoInputRef.current.value = '';
  };

  useEffect(() => {
    dispatch(fetchCategories());
    if (id) {
      dispatch(fetchPostById(id));
    }
    return () => {
      dispatch(clearCurrent());
      if (headlineBlobUrlRef.current) {
        URL.revokeObjectURL(headlineBlobUrlRef.current);
        headlineBlobUrlRef.current = null;
      }
      pendingInlineRef.current.forEach((_, blobUrl) => URL.revokeObjectURL(blobUrl));
      pendingInlineRef.current.clear();
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (post && isEdit) {
      setForm({
        title: post.title || '',
        body: post.body || '',
        category: post.category || '',
        isNewsletter: post.isNewsletter || false,
        headlineImage: post.headlineImage || null,
      });
    } else if (!isEdit && categories.length) {
      setForm((f) => (f.category ? f : { ...f, category: categories[0] }));
    }
  }, [post, isEdit, categories]);

  const handleHeaderMediaChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (headlineBlobUrlRef.current) {
      URL.revokeObjectURL(headlineBlobUrlRef.current);
    }
    const blobUrl = URL.createObjectURL(file);
    headlineBlobUrlRef.current = blobUrl;
    setHeadlinePreviewUrl(blobUrl);
    setHeadlineFile(file);
    setForm((f) => ({ ...f, headlineImage: null }));
    if (headerInputRef.current) headerInputRef.current.value = '';
  };

  const handleRemoveHeaderMedia = () => {
    if (headlineBlobUrlRef.current) {
      URL.revokeObjectURL(headlineBlobUrlRef.current);
      headlineBlobUrlRef.current = null;
    }
    setHeadlinePreviewUrl(null);
    setHeadlineFile(null);
    setForm((f) => ({ ...f, headlineImage: null }));
    if (headerInputRef.current) headerInputRef.current.value = '';
  };

  const uploadAllPendingMedia = async (bodyHtml) => {
    const pending = pendingInlineRef.current;
    const blobUrls = Array.from(pending.keys()).filter((url) => bodyHtml.includes(url));
    let finalHeadline = form.headlineImage;
    if (blobUrls.length === 0 && !headlineFile) return { body: bodyHtml, headlineImage: finalHeadline };

    let newBody = bodyHtml;
    const total = blobUrls.length + (headlineFile ? 1 : 0);
    let done = 0;

    const updateProgress = (phase, percent) => {
      setUploadStatus({ phase, current: done, total, percent });
    };

    if (headlineFile) {
      updateProgress('headline', 0);
      const result = await uploadPostMedia(
        headlineFile,
        'header',
        id || 'temp',
        (p) => updateProgress('headline', p)
      );
      done++;
      updateProgress('headline', 100);
      finalHeadline = {
        url: result.url,
        publicId: result.publicId,
        resourceType: result.resourceType || 'image',
      };
      setHeadlineFile(null);
      if (headlineBlobUrlRef.current) {
        URL.revokeObjectURL(headlineBlobUrlRef.current);
        headlineBlobUrlRef.current = null;
      }
      setHeadlinePreviewUrl(null);
    }

    for (let i = 0; i < blobUrls.length; i++) {
      const blobUrl = blobUrls[i];
      const entry = pending.get(blobUrl);
      if (!entry?.file) continue;
      const onProgress = (p) => setUploadStatus({
        phase: 'inline',
        current: done + (p >= 100 ? 1 : 0),
        total,
        percent: p,
      });
      updateProgress('inline', 0);
      const result = await uploadPostMedia(
        entry.file,
        'inline',
        id || 'temp',
        onProgress
      );
      const escaped = blobUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      newBody = newBody.replace(new RegExp(escaped, 'g'), result.url);
      pending.delete(blobUrl);
      URL.revokeObjectURL(blobUrl);
      done++;
    }

    return { body: newBody, headlineImage: finalHeadline };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) {
      enqueueSnackbar('Sélectionnez une catégorie.', { variant: 'error' });
      return;
    }

    setUploadStatus({ phase: 'preparing', current: 0, total: 1, percent: 0 });
    try {
      const { body: finalBody, headlineImage: finalHeadline } = await uploadAllPendingMedia(form.body);
      setUploadStatus(null);

      const payload = {
        title: form.title,
        body: finalBody,
        category: form.category,
        isNewsletter: form.isNewsletter,
        ...(finalHeadline && { headlineImage: finalHeadline }),
      };

      if (isEdit) {
        const result = await dispatch(updatePost({ id, ...payload }));
        if (updatePost.fulfilled.match(result)) {
          enqueueSnackbar('Article mis à jour.', { variant: 'success' });
          navigate('/dashboard/posts');
        } else {
          enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
        }
      } else {
        const result = await dispatch(createPost(payload));
        if (createPost.fulfilled.match(result)) {
          enqueueSnackbar('Article créé. Il est en attente de validation.', { variant: 'success' });
          navigate('/dashboard/posts');
        } else {
          enqueueSnackbar(result.payload || 'Erreur.', { variant: 'error' });
        }
      }
    } catch (err) {
      setUploadStatus(null);
      enqueueSnackbar(err.response?.data?.message || 'Erreur lors de l\'envoi.', { variant: 'error' });
    }
  };

  const headerPreviewUrl = headlinePreviewUrl || form.headlineImage?.url;
  const headerIsVideo = headlineFile
    ? headlineFile.type.startsWith('video/')
    : form.headlineImage?.resourceType === 'video';

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
        <IconButton
          onClick={() => navigate('/dashboard/posts')}
          disabled={!!uploadStatus}
          aria-label="Retour"
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          {isEdit ? "Modifier l'article" : "Créer un article"}
        </Typography>
      </Box>
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Titre"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />

            <TextField
              fullWidth
              select
              label="Catégorie"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              required
            >
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {getCategoryLabel(c)}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Image ou vidéo d'en-tête
              </Typography>
              <input
                ref={headerInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleHeaderMediaChange}
                style={{ display: 'none' }}
              />
              {headerPreviewUrl ? (
                <Box sx={{ position: 'relative', maxWidth: 600 }}>
                  {headerIsVideo ? (
                    <Box
                      component="video"
                      src={headerPreviewUrl}
                      controls
                      playsInline
                      sx={{
                        width: '100%',
                        maxHeight: 280,
                        borderRadius: 2,
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={headerPreviewUrl}
                      alt="En-tête"
                      sx={{
                        width: '100%',
                        maxHeight: 280,
                        objectFit: 'cover',
                        borderRadius: 2,
                      }}
                    />
                  )}
                  {headlineFile && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      En attente d'envoi
                    </Typography>
                  )}
                  <IconButton
                    onClick={handleRemoveHeaderMedia}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => headerInputRef.current?.click()}
                  disabled={!!uploadStatus}
                  sx={{ textTransform: 'none' }}
                >
                  Choisir une image ou vidéo
                </Button>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Contenu
              </Typography>
              <Box
                sx={{
                  '& .quill': { bgcolor: 'background.paper' },
                  '& .ql-toolbar': {
                    borderTopLeftRadius: 1,
                    borderTopRightRadius: 1,
                    bgcolor: 'grey.50',
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                  },
                  '& .ql-container': {
                    borderBottomLeftRadius: 1,
                    borderBottomRightRadius: 1,
                    minHeight: { xs: 220, sm: 280 },
                    fontSize: '1rem',
                  },
                  '& .ql-editor': { minHeight: { xs: 200, sm: 260 } },
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.body}
                  onChange={(v) => setForm((f) => ({ ...f, body: v }))}
                  modules={modules}
                  formats={formats}
                  placeholder="Rédigez votre article…"
                  readOnly={!!uploadStatus}
                />
              </Box>
            </Box>

            {uploadStatus && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {uploadStatus.phase === 'preparing'
                    ? "Préparation de l'envoi…"
                    : uploadStatus.phase === 'headline'
                    ? `Envoi du média d'en-tête… ${uploadStatus.percent}%`
                    : uploadStatus.phase === 'inline'
                    ? `Envoi des médias (${uploadStatus.current}/${uploadStatus.total})… ${uploadStatus.percent}%`
                    : "Envoi en cours…"}
                </Typography>
                <LinearProgress
                  variant={uploadStatus.percent > 0 ? 'determinate' : 'indeterminate'}
                  value={uploadStatus.percent}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </Paper>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isNewsletter}
                  onChange={(e) => setForm((f) => ({ ...f, isNewsletter: e.target.checked }))}
                />
              }
              label="Envoyer en newsletter"
            />
          </Box>

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={loading || !!uploadStatus}>
              {loading && !uploadStatus ? <CircularProgress size={24} /> : isEdit ? 'Enregistrer' : 'Publier'}
            </Button>
            <Button onClick={() => navigate('/dashboard/posts')} disabled={!!uploadStatus}>
              Annuler
            </Button>
          </Box>
        </form>
      </Paper>

      <Dialog
        open={embedDialogOpen}
        onClose={() => setEmbedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2.5,
            px: 3,
            background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <YouTubeIcon sx={{ fontSize: 32 }} />
          Insérer une vidéo
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                border: '1px solid',
                borderColor: 'primary.light',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <YouTubeIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    Lien YouTube ou Vimeo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Collez l'URL de la vidéo
                  </Typography>
                </Box>
              </Box>
              <TextField
                fullWidth
                placeholder="https://www.youtube.com/watch?v=..."
                value={embedUrl}
                onChange={(e) => setEmbedUrl(e.target.value)}
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                  },
                }}
              />
              <Button
                variant="contained"
                color="error"
                onClick={handleEmbedVideo}
                disabled={!embedUrl.trim()}
                fullWidth
                sx={{ mt: 2, py: 1.25, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                Intégrer la vidéo
              </Button>
            </Box>

            <Divider sx={{ my: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                ou
              </Typography>
            </Divider>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'divider',
                textAlign: 'center',
                transition: 'border-color 0.2s, bgcolor 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(25, 118, 210, 0.02)',
                },
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <VideoFileIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
              </Box>
              <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>
                Télécharger une vidéo
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                MP4, WebM — jusqu'à 200 Mo
              </Typography>
              <input
                ref={embedVideoInputRef}
                type="file"
                accept="video/*"
                onChange={handleAddVideoFromDialog}
                style={{ display: 'none' }}
                id="postform-embed-video-file"
              />
              <Button
                variant="outlined"
                component="label"
                htmlFor="postform-embed-video-file"
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: 'none', borderRadius: 1.5 }}
              >
                Choisir un fichier
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Button onClick={() => setEmbedDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
